import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Upload } from "lucide-react";
import { InvoiceUploader } from "@/components/invoices/InvoiceUploader";
import { ManualInputForm } from "@/components/forms/ManualInputForm";
import { InvoiceList } from "@/components/invoices/InvoiceList";
import { KeyMetrics } from "@/components/dashboard/KeyMetrics";
import { BudgetAlert } from "@/components/dashboard/BudgetAlert";
import { APREditModal } from "@/components/modals/APREditModal";
import { AboutModal } from "@/components/modals/AboutModal";
import { FeedbackModal } from "@/components/modals/FeedbackModal";
import { HelpModal } from "@/components/modals/HelpModal";
import { IntegrationsModal } from "@/components/modals/IntegrationsModal";
import { historicalInvoiceData } from "@/components/dashboard/HistoricalTab";
import { InsightsTab } from "@/components/dashboard/InsightsTab";
import { RecommendationsPanel } from "@/components/dashboard/RecommendationsPanel";
import { DocumentationModal } from "@/components/modals/DocumentationModal";
import { HowItWorksModal } from "@/components/modals/HowItWorksModal";
import { PaymentRunModal } from "@/components/modals/PaymentRunModal";
import { SavingsVisualization } from "@/components/charts/SavingsVisualization";
import { ChatPanel } from "@/components/ChatPanel";
import { ACHHistoryModal } from "@/components/modals/ACHHistoryModal";
import { BudgetDetailModal } from "@/components/modals/BudgetDetailModal";
import { IntegrationsTab } from "@/components/dashboard/IntegrationsTab";
import { StrategizeTab } from "@/components/dashboard/StrategizeTab";

import { ActivityLog } from "@/components/dashboard/ActivityLog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { useToast } from "@/hooks/use-toast";
import { useSettings } from "@/hooks/useSettings";
import { useAuth } from "@/hooks/useAuth";
import { useInvoices } from "@/hooks/useInvoices";
import { useModals } from "@/hooks/useModals";
import { useBudget } from "@/hooks/useBudget";
import { useACH } from "@/hooks/useACH";
import { Header } from "@/components/layout/Header";
import { getGreeting } from "@/lib/greeting";
import { formatCurrency, formatCurrencyInput } from "@/lib/currency";
import { Invoice, ParsedInvoice, isAcceleratedStatus, ACH_READY_STATUS } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Calendar as CalendarIcon, CreditCard, Percent } from "lucide-react";
import { Slider } from "@/components/ui/slider";

export default function Index() {
  const { profile, signOut, companyId, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect company-scoped users to the backtest page
  useEffect(() => {
    if (!isAuthLoading && companyId) {
      navigate("/backtest", { replace: true });
    }
  }, [companyId, isAuthLoading, navigate]);
  const { invoices, isLoading: isLoadingInvoices, refetch: fetchInvoices } = useInvoices();
  const modals = useModals();
  const [apr, setApr] = useState(12);
  const [isUploading, setIsUploading] = useState(false);
  const [parsedInvoice, setParsedInvoice] = useState<ParsedInvoice | null>(null);
  const { toast } = useToast();
  const { settings, updateSettings } = useSettings();
  const lastAlertInvoiceRef = useRef<string | null>(null);

  const [pendingBudget, setPendingBudget] = useState(settings?.early_payment_budget || 100000);
  const [pendingPaymentDays, setPendingPaymentDays] = useState(settings?.early_payment_days || 5);
  const [activeTab, setActiveTab] = useState("insights");

  const {
    earlyPaymentBudget,
    usedBudget,
    availableBudget,
    pendingInvoices,
    pendingTotal,
    potentialSavings,
  } = useBudget(invoices);

  const invoicesForACH = useMemo(
    () => invoices.filter((inv) => inv.status === ACH_READY_STATUS),
    [invoices]
  );
  const { isGeneratingACH, achHistory, generateACHFile } = useACH(invoicesForACH, fetchInvoices);

  // Merge real + historical for metrics/charts
  const allInvoicesWithHistory = useMemo(() => {
    const realIds = new Set(invoices.map((i) => i.id));
    return [...invoices, ...historicalInvoiceData.filter((h) => !realIds.has(h.id))] as Invoice[];
  }, [invoices]);

  // APR stats for insights
  const { acceptRate, acceleratedAll } = useMemo(() => {
    const allActivity = allInvoicesWithHistory.filter((i) => i.status !== "pending");
    const accelerated = allActivity.filter((i) => isAcceleratedStatus(i.status));
    const rate = allActivity.length > 0 ? (accelerated.length / allActivity.length) * 100 : 0;
    return { acceptRate: rate, acceleratedAll: accelerated };
  }, [allInvoicesWithHistory]);

  // Missed savings = declined invoices' discount + budget-blocked pending invoices' potential
  const missedSavings = useMemo(
    () =>
      allInvoicesWithHistory
        .filter((i) => i.status === "standard_payment")
        .reduce((sum, i) => sum + (i.discount_amount || 0), 0) + potentialSavings,
    [allInvoicesWithHistory, potentialSavings]
  );

  // Budget alert effect
  useEffect(() => {
    if (pendingInvoices.length === 0 || !settings?.notification_email) return;
    const mostRecentPending = pendingInvoices.reduce((latest, inv) =>
      new Date(inv.created_at) > new Date(latest.created_at) ? inv : latest
    );
    if (lastAlertInvoiceRef.current !== mostRecentPending.id) {
      lastAlertInvoiceRef.current = mostRecentPending.id;
      invokeEdgeFunction("send-budget-alert", {
        recipientEmail: settings.notification_email,
        buyerName: settings.buyer_name,
        availableBudget,
        blockedInvoicesCount: pendingInvoices.length,
        potentialSavings,
        totalBlocked: pendingTotal,
      }).catch((error) => {
        console.error("Failed to send budget alert:", error);
      });
    }
  }, [pendingInvoices, settings]);

  const [pendingFileInfo, setPendingFileInfo] = useState<{
    file_url?: string;
    file_name?: string;
  } | null>(null);
  const [recentlyAddedInvoiceIds, setRecentlyAddedInvoiceIds] = useState<string[]>([]);

  const submitInvoice = async (invoiceData: {
    payment_amount: number;
    payment_due_date: string;
    supplier_name: string;
    supplier_email: string;
    file_url?: string;
    file_name?: string;
  }) => {
    try {
      // KAY-66: Reject zero, negative, or unreasonably large amounts
      if (!invoiceData.payment_amount || invoiceData.payment_amount <= 0) {
        toast({
          title: "Invalid amount",
          description: "Invoice amount must be greater than $0.",
          variant: "destructive",
        });
        return;
      }
      if (invoiceData.payment_amount > 100_000_000) {
        toast({
          title: "Amount too large",
          description: "Invoice amount cannot exceed $100,000,000. Please verify.",
          variant: "destructive",
        });
        return;
      }

      // KAY-68: Reject past due dates — they produce $0 discount and the edge function will reject the email anyway
      const dueDateCheck = new Date(invoiceData.payment_due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (isNaN(dueDateCheck.getTime()) || dueDateCheck < today) {
        toast({
          title: "Invalid due date",
          description: "Invoice due date must be today or in the future.",
          variant: "destructive",
        });
        return;
      }

      // Warn (non-blocking) for large invoice amounts
      if (invoiceData.payment_amount > 1_000_000) {
        const formatted = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(invoiceData.payment_amount);
        toast({
          title: "Large invoice amount",
          description: `Large invoice amount (${formatted}) — please verify before submitting.`,
        });
      }

      const dueDate = new Date(invoiceData.payment_due_date);
      const paymentDate = new Date();
      paymentDate.setDate(paymentDate.getDate() + (settings?.early_payment_days || 5));

      const daysEarly = Math.max(
        0,
        Math.floor((dueDate.getTime() - paymentDate.getTime()) / (1000 * 60 * 60 * 24))
      );
      const discountRate = (apr / 100) * (daysEarly / 365);
      const discountAmount = Math.round(invoiceData.payment_amount * discountRate * 100) / 100;
      const discountedPaymentAmount =
        Math.round((invoiceData.payment_amount - discountAmount) * 100) / 100;

      const { data: insertedInvoice, error: insertError } = await supabase
        .from("invoices")
        .insert({
          payment_amount: invoiceData.payment_amount,
          payment_due_date: invoiceData.payment_due_date,
          supplier_name: invoiceData.supplier_name,
          supplier_email: invoiceData.supplier_email,
          apr_rate: apr,
          discount_amount: discountAmount,
          discounted_payment_amount: discountedPaymentAmount,
          status: "pending",
          invoice_file_url: invoiceData.file_url || null,
          invoice_file_name: invoiceData.file_name || null,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setRecentlyAddedInvoiceIds((prev) => [...prev, insertedInvoice.id]);
      setTimeout(() => {
        setRecentlyAddedInvoiceIds((prev) => prev.filter((id) => id !== insertedInvoice.id));
      }, 4500);

      const { data: emailResult, error: emailError } = await supabase.functions.invoke(
        "send-discount-offer",
        {
          body: {
            invoiceId: insertedInvoice.id,
            supplierEmail: invoiceData.supplier_email,
            supplierName: invoiceData.supplier_name,
            originalAmount: invoiceData.payment_amount,
            discountedAmount: discountedPaymentAmount,
            discountAmount: discountAmount,
            dueDate: invoiceData.payment_due_date,
            aprRate: apr,
            buyerName: settings?.buyer_name || "Your Buyer",
            invoiceFileName: invoiceData.file_name,
            invoiceFileUrl: invoiceData.file_url,
          },
        }
      );

      if (emailError) {
        await supabase
          .from("invoices")
          .update({ email_error: emailError.message || "Failed to send email" })
          .eq("id", insertedInvoice.id);
        toast({
          title: "Email failed",
          description: "Invoice saved but email could not be sent.",
          variant: "destructive",
        });
      } else if (emailResult?.error === "Insufficient budget") {
        await supabase
          .from("invoices")
          .update({ email_error: "Insufficient budget - email not sent" })
          .eq("id", insertedInvoice.id);
        toast({
          title: "Insufficient budget",
          description: "Invoice saved but email not sent.",
          variant: "destructive",
        });
      } else {
        await supabase
          .from("invoices")
          .update({ email_sent_at: new Date().toISOString() })
          .eq("id", insertedInvoice.id);
        toast({
          title: "Invoice submitted",
          description: "Discount offer email sent to supplier.",
        });
      }

      modals.setShowManualInput(false);
      setParsedInvoice(null);
      setPendingFileInfo(null);
      modals.setShowUploadModal(false);
      fetchInvoices();
    } catch (error) {
      console.error("Error submitting invoice:", error);
      toast({
        title: "Failed to submit invoice",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleInvoiceParsed = (data: ParsedInvoice, needsManualInput: boolean) => {
    setParsedInvoice(data);
    setPendingFileInfo({ file_url: data.file_url, file_name: data.file_name });
    if (needsManualInput) {
      modals.setShowUploadModal(false);
      modals.setShowManualInput(true);
    } else {
      submitInvoice({
        payment_amount: data.payment_amount!,
        payment_due_date: data.payment_due_date!,
        supplier_name: data.supplier_name!,
        supplier_email: data.supplier_email!,
        file_url: data.file_url,
        file_name: data.file_name,
      });
    }
  };

  const handleManualSubmit = (data: {
    payment_amount: number | null;
    payment_due_date: string | null;
    supplier_name: string | null;
    supplier_email: string | null;
  }) => {
    submitInvoice({
      payment_amount: data.payment_amount!,
      payment_due_date: data.payment_due_date!,
      supplier_name: data.supplier_name!,
      supplier_email: data.supplier_email!,
      file_url: pendingFileInfo?.file_url,
      file_name: pendingFileInfo?.file_name,
    });
  };

  const handleEarlyPaymentBudgetChange = async (budget: number) => {
    const previousBudget = settings?.early_payment_budget || 0;
    await updateSettings({ early_payment_budget: budget });

    if (budget > previousBudget) {
      const unsentInvoices = invoices.filter(
        (inv) => inv.status === "pending" && inv.email_error?.includes("Insufficient budget")
      );
      if (unsentInvoices.length > 0) {
        const sortedInvoices = [...unsentInvoices].sort(
          (a, b) => (b.discount_amount || 0) - (a.discount_amount || 0)
        );
        let sentCount = 0;
        for (const invoice of sortedInvoices) {
          const { data: emailResult, error: emailError } = await supabase.functions.invoke(
            "send-discount-offer",
            {
              body: {
                invoiceId: invoice.id,
                supplierEmail: invoice.supplier_email,
                supplierName: invoice.supplier_name,
                originalAmount: invoice.payment_amount,
                discountedAmount: invoice.discounted_payment_amount,
                discountAmount: invoice.discount_amount,
                dueDate: invoice.payment_due_date,
                aprRate: invoice.apr_rate,
                buyerName: settings?.buyer_name || "Your Buyer",
              },
            }
          );
          if (!emailError && !emailResult?.error) {
            await supabase
              .from("invoices")
              .update({ email_sent_at: new Date().toISOString(), email_error: null })
              .eq("id", invoice.id);
            sentCount++;
          } else if (emailResult?.error === "Insufficient budget") {
            break;
          }
        }
        if (sentCount > 0) {
          toast({
            title: "Offers sent",
            description: `${sentCount} offer(s) sent with available budget.`,
          });
          fetchInvoices();
        }
      }
    }
  };

  const handleSaveBudget = () => {
    handleEarlyPaymentBudgetChange(pendingBudget);
    modals.setShowBudgetModal(false);
  };

  const handleQuickAdd = (amount: number) => {
    setPendingBudget((prev) => prev + amount);
  };

  const handleSavePaymentWindow = () => {
    updateSettings({ early_payment_days: pendingPaymentDays });
    modals.setShowPaymentWindowModal(false);
    toast({
      title: "Payment minimum updated",
      description: `Early payment minimum set to ${pendingPaymentDays} days.`,
    });
  };

  const displayName = profile?.display_name || settings?.buyer_name || "there";

  const [chatCollapsed, setChatCollapsed] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Header onShowACHHistory={() => modals.setShowACHHistoryModal(true)} onSignOut={signOut} />

      {/* Main Content */}
      <main
        className={`w-full px-6 py-6 space-y-5 transition-[padding] duration-300 ease-in-out ${chatCollapsed ? "pr-14" : "pr-[calc(25vw+1.5rem)]"}`}
      >
        {/* Greeting + Top-level Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-display font-bold text-foreground tracking-tight">
              {getGreeting()}, {displayName}
            </h2>
            <p className="text-sm text-muted-foreground">Here's your early payment dashboard</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
          <TabsList className="bg-muted/60 p-1">
            <TabsTrigger
              value="insights"
              className="text-sm px-6 data-[state=active]:bg-white/[0.08]"
            >
              Insights
            </TabsTrigger>
            <TabsTrigger
              value="strategize"
              className="text-sm px-6 data-[state=active]:bg-white/[0.08]"
            >
              Strategize
            </TabsTrigger>
            <TabsTrigger
              value="optimize"
              className="text-sm px-6 data-[state=active]:bg-white/[0.08]"
            >
              Optimize
            </TabsTrigger>
            <TabsTrigger
              value="integrations"
              className="text-sm px-6 data-[state=active]:bg-white/[0.08]"
            >
              Integrations
            </TabsTrigger>
          </TabsList>

          {/* Insights Tab - Learn phase: where are you leaving money on the table */}
          <TabsContent value="insights" className="space-y-5">
            <InsightsTab
              totalSaved={21847}
              dollarsAccelerated={1023600}
              earlyPaymentBudget={100000}
              usedBudget={21847}
              availableBudget={78153}
              captureRate={acceptRate}
              missedSavings={missedSavings}
              paymentWindowDays={settings?.early_payment_days ?? 5}
              currentApr={apr}
              budgetPeriodStart={settings?.budget_period_start}
              budgetPeriodEnd={settings?.budget_period_end}
              invoicesForChart={invoices}
              historicalInvoicesForChart={historicalInvoiceData}
              allInvoices={invoices}
              pendingInvoices={pendingInvoices}
            />
          </TabsContent>

          {/* Optimize Tab - Act phase: operator invoice management */}
          <TabsContent value="optimize" className="space-y-5">
            {/* Discounting action bar + condensed APR */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setPendingPaymentDays(settings?.early_payment_days || 5);
                    modals.setShowPaymentWindowModal(true);
                  }}
                >
                  <CalendarIcon className="w-3.5 h-3.5 mr-1" />
                  Min: {settings?.early_payment_days || 5}d
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => modals.setShowAprModal(true)}
                >
                  <Percent className="w-3.5 h-3.5 mr-1.5" />
                  Set APR: {apr}%
                </Button>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => modals.setShowUploadModal(true)}
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Manual Upload
                </Button>
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => modals.setShowACHHistoryModal(true)}
                >
                  <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                  Generate ACH & Pay
                  {invoicesForACH.length > 0 && (
                    <span className="ml-1 text-[10px] bg-primary-foreground/20 px-1.5 py-0.5 rounded-full font-bold">
                      {invoicesForACH.length}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Budget Alert */}
            {pendingInvoices.length > 0 && (
              <BudgetAlert
                availableBudget={availableBudget}
                pendingInvoicesTotal={pendingTotal}
                blockedInvoicesCount={pendingInvoices.length}
                potentialSavings={potentialSavings}
                currentBudget={earlyPaymentBudget}
                onBudgetChange={handleEarlyPaymentBudgetChange}
              />
            )}

            {/* Metrics row (no Suppliers Enrolled) */}
            <KeyMetrics
              invoices={invoices}
              onInvoicesUpdated={fetchInvoices}
              availableBudget={availableBudget}
              budgetPeriodStart={settings?.budget_period_start}
              budgetPeriodEnd={settings?.budget_period_end}
              onBudgetClick={() => modals.setShowBudgetDetailModal(true)}
              onPaymentRunClick={() => modals.setShowPaymentRunModal(true)}
              onSavingsClick={() => modals.setShowSavingsModal(true)}
              allInvoices={invoices}
              hideSuppliers
            />

            {/* Recommendations panel — Strategize phase */}
            <RecommendationsPanel
              invoices={invoices}
              availableBudget={availableBudget}
              potentialSavings={potentialSavings}
              pendingInvoices={pendingInvoices}
              onPayClick={() => modals.setShowACHHistoryModal(true)}
              onBudgetClick={() => modals.setShowBudgetDetailModal(true)}
            />

            {/* Two-column: Invoices (left) | Activity Log (right) */}
            <div className="grid lg:grid-cols-2 gap-6">
              <InvoiceList
                invoices={invoices}
                isLoading={isLoadingInvoices}
                availableBudget={availableBudget}
                highlightedInvoiceIds={recentlyAddedInvoiceIds}
              />
              <ActivityLog invoices={allInvoicesWithHistory} />
            </div>
          </TabsContent>

          {/* Strategize Tab - Combined Outstanding + Optimize from PAR Systems */}
          <TabsContent value="strategize" className="space-y-5">
            <StrategizeTab />
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations">
            <IntegrationsTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <AboutModal open={modals.showAboutModal} onOpenChange={modals.setShowAboutModal} />
      <FeedbackModal open={modals.showFeedbackModal} onOpenChange={modals.setShowFeedbackModal} />
      <HelpModal open={modals.showHelpModal} onOpenChange={modals.setShowHelpModal} />
      <IntegrationsModal
        open={modals.showIntegrationsModal}
        onOpenChange={modals.setShowIntegrationsModal}
      />
      <DocumentationModal
        open={modals.showDocumentationModal}
        onOpenChange={modals.setShowDocumentationModal}
      />
      <HowItWorksModal
        open={modals.showHowItWorksModal}
        onOpenChange={modals.setShowHowItWorksModal}
        apr={apr}
      />
      <APREditModal
        open={modals.showAprModal}
        onOpenChange={modals.setShowAprModal}
        apr={apr}
        onAprChange={setApr}
        onHowItWorks={() => modals.setShowHowItWorksModal(true)}
        paymentWindowDays={settings?.early_payment_days ?? 5}
        pipelineInvoiceCount={invoices.filter((inv) => inv.status === "pending").length}
        pipelineTotalAmount={invoices
          .filter((inv) => inv.status === "pending")
          .reduce((sum, inv) => sum + inv.payment_amount, 0)}
        yourAcceptRate={acceptRate}
        acceleratedCount={acceleratedAll.length}
      />
      <PaymentRunModal
        open={modals.showPaymentRunModal}
        onOpenChange={modals.setShowPaymentRunModal}
        invoices={invoices}
      />
      <SavingsVisualization
        open={modals.showSavingsModal}
        onOpenChange={modals.setShowSavingsModal}
        invoices={allInvoicesWithHistory}
      />
      <ACHHistoryModal
        open={modals.showACHHistoryModal}
        onOpenChange={modals.setShowACHHistoryModal}
        achHistory={achHistory}
        readyCount={invoicesForACH.length}
        isGenerating={isGeneratingACH}
        onGenerate={generateACHFile}
      />

      <BudgetDetailModal
        open={modals.showBudgetDetailModal}
        onOpenChange={modals.setShowBudgetDetailModal}
        totalBudget={earlyPaymentBudget}
        usedBudget={usedBudget}
        availableBudget={availableBudget}
        invoices={invoices}
        budgetPeriodStart={settings?.budget_period_start}
        budgetPeriodEnd={settings?.budget_period_end}
      />

      {/* Upload Modal */}
      <Dialog open={modals.showUploadModal} onOpenChange={modals.setShowUploadModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manually Upload Invoices</DialogTitle>
            <DialogDescription>
              Upload PDF invoices to create early payment discount offers
            </DialogDescription>
          </DialogHeader>
          <InvoiceUploader
            onInvoiceParsed={handleInvoiceParsed}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            onBatchComplete={() => modals.setShowUploadModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Manual Input Modal */}
      <Dialog open={modals.showManualInput} onOpenChange={modals.setShowManualInput}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete Invoice Details</DialogTitle>
            <DialogDescription>Fill in the missing information.</DialogDescription>
          </DialogHeader>
          {parsedInvoice && (
            <ManualInputForm
              parsedData={parsedInvoice}
              onSubmit={handleManualSubmit}
              onCancel={() => {
                modals.setShowManualInput(false);
                setParsedInvoice(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Budget Edit Modal */}
      <Dialog open={modals.showBudgetModal} onOpenChange={modals.setShowBudgetModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Early Payment Budget</DialogTitle>
            <DialogDescription>
              Set your budget. Budget-blocked invoices will be automatically processed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newBudget">Budget Amount</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  id="newBudget"
                  type="text"
                  value={pendingBudget.toLocaleString()}
                  onChange={(e) => setPendingBudget(formatCurrencyInput(e.target.value))}
                  className="bg-background"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Current: {formatCurrency(settings?.early_payment_budget || 100000)}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Quick Add</Label>
              <div className="flex gap-2 flex-wrap">
                {[10000, 25000, 50000, 100000].map((amt) => (
                  <Button key={amt} variant="outline" size="sm" onClick={() => handleQuickAdd(amt)}>
                    <Plus className="w-3 h-3 mr-1" />${(amt / 1000).toFixed(0)}K
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => modals.setShowBudgetModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveBudget}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Window Modal */}
      <Dialog open={modals.showPaymentWindowModal} onOpenChange={modals.setShowPaymentWindowModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Early Payment Minimum</DialogTitle>
            <DialogDescription>
              The minimum number of days before you can pay suppliers early.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-4xl font-display font-bold text-primary">{pendingPaymentDays}</p>
              <p className="text-sm text-muted-foreground">days</p>
            </div>
            <Slider
              value={[pendingPaymentDays]}
              onValueChange={(v) => setPendingPaymentDays(v[0])}
              min={1}
              max={30}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 day</span>
              <span>15 days</span>
              <span>30 days</span>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              This is the earliest you can pay suppliers. Shorter minimums increase discount rates.
              Most programs use 5–10 days.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => modals.setShowPaymentWindowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePaymentWindow}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ChatPanel page="overview" onCollapsedChange={setChatCollapsed} />
    </div>
  );
}
